import type { FaixaLongitudinal, PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { MaterialPeca, Peca } from '../componentes/Peca'
import { Rotulo3D } from '../componentes/Rotulo3D'
import { useMaquete } from '../componentes/contextoMaquete'
import { useDeslocamentoRigido } from './contextoDeformacao'

/**
 * Zonas de absorção/deformação como volumes didáticos.
 *
 * O volume mostra ONDE a energia deveria ser absorvida antes de chegar ao
 * habitáculo. Na Cinemática, com colisão, a face externa da zona recua junto
 * com o para-choque: o que sobra do volume é a absorção ainda não consumida.
 */
export function ZonasColapsaveis({ perfil }: { perfil: PerfilVeiculo }) {
  const { mostrarRotulos, camadas } = useMaquete()
  return (
    <group>
      <Zona
        id="zonaAbsorcaoDianteira"
        perfil={perfil}
        faixa={perfil.zonaAbsorcaoDianteira}
        nome="absorção dianteira"
        mostrarRotulo={mostrarRotulos && camadas.zonaDeformacao}
      />
      <Zona
        id="zonaAbsorcaoTraseira"
        perfil={perfil}
        faixa={perfil.zonaAbsorcaoTraseira}
        nome="absorção traseira"
        mostrarRotulo={mostrarRotulos && camadas.zonaDeformacao}
      />
    </group>
  )
}

function Zona({
  id,
  perfil,
  faixa,
  nome,
  mostrarRotulo,
}: {
  id: string
  perfil: PerfilVeiculo
  faixa: FaixaLongitudinal
  nome: string
  mostrarRotulo: boolean
}) {
  // a zona "gasta" na colisão: a face externa recua junto com o para-choque,
  // e o volume que sobra mostra quanto da absorção foi consumido
  const dianteira = faixa.zDianteiro >= perfil.frenteZ - 1e-6
  const recuo = useDeslocamentoRigido([0, (perfil.pisoY + perfil.capoY) / 2, dianteira ? faixa.zDianteiro : faixa.zTraseiro], 'paraChoque')[2]
  const zDianteiro = dianteira ? faixa.zDianteiro + Math.min(0, recuo) : faixa.zDianteiro
  const zTraseiro = dianteira ? faixa.zTraseiro : faixa.zTraseiro + Math.max(0, recuo)
  const profundidade = Math.max(0.02, Math.abs(zDianteiro - zTraseiro))
  const z = (zDianteiro + zTraseiro) / 2
  // a zona ocupa da longarina à linha do capô: mais alta que isso ela engole
  // o veículo e deixa de comunicar "onde a energia é absorvida"
  const yBase = perfil.pisoY - 0.12
  const altura = perfil.capoY - 0.08 - yBase
  const y = yBase + altura / 2
  const largura = perfil.largura - 0.28

  return (
    <Peca id={id}>
      <mesh position={[0, y, z]}>
        <boxGeometry args={[largura, altura, profundidade]} />
        <MaterialPeca id={id} metalico={0} rugosidade={0.8} />
      </mesh>
      {/* aramado para a zona ler como volume, não como caixa sólida */}
      <mesh position={[0, y, z]}>
        <boxGeometry args={[largura + 0.005, altura + 0.005, profundidade + 0.005]} />
        <meshBasicMaterial wireframe color="#f7d98a" transparent opacity={0.3} />
      </mesh>
      {mostrarRotulo && <Rotulo3D posicao={[0, y + altura / 2 + 0.12, z]} texto={nome} variante="zona" fator={4.5} />}
    </Peca>
  )
}
